import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';

/** The visible rows of the search result the user last looked at; each row's detail page is `${basePath}/${id}`. */
interface SearchNavigationSource {
  basePath: string;
  ids: string[];
}

type SetSearchNavigationSource = (source: SearchNavigationSource) => void;

/** A row as reported by Table's onFilteredRowsChange; only the id is used. */
interface SearchNavigationRow {
  id?: unknown;
}

const SearchNavigationSourceContext = createContext<SearchNavigationSource | undefined>(undefined);
// Separate context so list pages that only register don't re-render when the source changes.
const SetSearchNavigationSourceContext = createContext<SetSearchNavigationSource | undefined>(undefined);

export const SearchNavigationProvider = ({ children }: { children: React.ReactNode }) => {
  const [source, setSource] = useState<SearchNavigationSource>();

  return (
    <SetSearchNavigationSourceContext.Provider value={setSource}>
      <SearchNavigationSourceContext.Provider value={source}>{children}</SearchNavigationSourceContext.Provider>
    </SetSearchNavigationSourceContext.Provider>
  );
};

const useSetSearchNavigationSource = () => {
  const setSource = useContext(SetSearchNavigationSourceContext);
  if (!setSource) {
    throw new Error('useSearchNavigation hooks must be used within a SearchNavigationProvider');
  }
  return setSource;
};

/**
 * Registers a search result page as the source for next/back navigation on its detail pages.
 * Returns a callback to pass to Table's `onFilteredRowsChange`, so the visible (filtered) rows are browsed.
 */
export const useSearchNavigationSource = (basePath: string) => {
  const setSource = useSetSearchNavigationSource();
  const { pathname } = useLocation();

  return useCallback(
    (rows: SearchNavigationRow[]) => {
      // nuqs resets the search params synchronously on pushState, so the list re-renders with default
      // params after the user has navigated away; that result must not replace the one they came from.
      if (window.location.pathname !== pathname) return;
      const ids = rows.flatMap(({ id }) => (typeof id === 'number' || typeof id === 'string' ? [String(id)] : []));
      setSource({ basePath, ids });
    },
    [basePath, pathname, setSource]
  );
};

/** Next/back within the registered search result, for the detail page showing `id`. */
export const useSearchNavigation = (id: string | undefined) => {
  useSetSearchNavigationSource();
  const source = useContext(SearchNavigationSourceContext);
  const navigate = useNavigate();
  const { pathname } = useLocation();

  // Only browse when this page is the detail page of a row in the source, so a stale list from an
  // unrelated search (whose numeric ids may collide with this entity's) never shows up.
  const index = source && id !== undefined && `${source.basePath}/${id}` === pathname ? source.ids.indexOf(id) : -1;
  const hasPrev = index > 0;
  const hasNext = index > -1 && source !== undefined && index < source.ids.length - 1;

  const goTo = useCallback(
    (offset: number) => {
      const targetId = source && index > -1 ? source.ids[index + offset] : undefined;
      if (!source || targetId === undefined) return;
      navigate(`${source.basePath}/${targetId}`, { replace: true });
    },
    [source, index, navigate]
  );

  return useMemo(() => ({ hasPrev, hasNext, goPrev: () => goTo(-1), goNext: () => goTo(1) }), [hasPrev, hasNext, goTo]);
};
